
import { useState } from "react";
import { toast } from "sonner";
import { useLogoMutation } from "./useLogoMutation";
import { handleFileUpload, handleUrlUpload } from "./utils/logoUploadHandler";
import { logger } from "@/services/LoggingService";

const COMPONENT = 'CompanyLogo';

interface UseCompanyLogoProps {
  companyId: string;
  onComplete?: (newUrl: string | null) => void;
}

export function useCompanyLogo({ companyId, onComplete }: UseCompanyLogoProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [logoUrlInput, setLogoUrlInput] = useState('');
  const { mutateAsync: updateLogo } = useLogoMutation(companyId);

  const handleSuccess = async (publicUrl: string) => {
    await updateLogo(publicUrl);
    onComplete?.(publicUrl);
    toast.success("Logo updated successfully");
  };

  const handleLogoRemove = async (): Promise<boolean> => {
    try {
      logger.info(COMPONENT, 'Starting logo removal', { 
        companyId 
      });
      
      setIsUploading(true);
      await updateLogo(null);
      onComplete?.(null);

      logger.info(COMPONENT, 'Logo removal successful', { 
        companyId 
      });

      toast.success("Logo removed successfully");
      return true;
    } catch (error: any) {
      logger.error(COMPONENT, 'Logo removal failed', {
        error: error.message,
        details: error,
        companyId
      });
      
      toast.error(error.message || "Failed to remove logo");
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    isUploading,
    logoUrlInput,
    setLogoUrlInput,
    handleFileUpload: (file: File) => handleFileUpload(file, {
      companyId,
      onSuccess: handleSuccess,
      setIsUploading
    }),
    handleLogoUrlUpdate: (logoUrl: string) => handleUrlUpload(logoUrl, {
      companyId,
      onSuccess: handleSuccess,
      setIsUploading
    }),
    handleLogoRemove
  };
}
